import { AlertTriangle, Check, CircleSlash, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { get } from '../api';
import { Spinner } from './base';

/**
 * Launch readiness, per level of the product standard. Owner-only: it says
 * what is not yet true about a product, which is snip.work's business and not
 * something to show a client.
 */

export type CheckState = 'pass' | 'warn' | 'fail' | 'skip';

interface Check { key: string; label: string; state: CheckState; detail: string }
interface Level { level: number; title: string; state: CheckState; checks: Check[] }
export interface ReadinessData { levels: Level[]; state: CheckState; checked_at: string }

const STATE_STYLE: Record<CheckState, { icon: typeof Check; className: string; label: string }> = {
  pass: { icon: Check, className: 'text-emerald-600 dark:text-emerald-400', label: 'Ready' },
  warn: { icon: AlertTriangle, className: 'text-amber-600 dark:text-amber-400', label: 'Needs attention' },
  fail: { icon: X, className: 'text-red-600 dark:text-red-400', label: 'Not ready' },
  // Covers both "does not apply here" and "cannot be determined from outside".
  skip: { icon: CircleSlash, className: 'text-ink-400 dark:text-ink-500', label: 'Not checked' },
};

export function ReadinessBadge({ state }: { state: CheckState }) {
  const style = STATE_STYLE[state];
  const Icon = style.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${style.className}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {style.label}
    </span>
  );
}

export function ReadinessPanel({ productId }: { productId: string }) {
  const [data, setData] = useState<ReadinessData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    get<ReadinessData>(`/products/${productId}/readiness`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not run the check.'))
      .finally(() => setLoading(false));
  }, [productId]);

  useEffect(load, [load]);

  return (
    <section className="card border-dashed p-5">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
          Launch readiness
        </h2>
        <div className="flex items-center gap-3">
          {data && <ReadinessBadge state={data.state} />}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium
                       text-ink-500 hover:bg-paper-200 hover:text-ink-700
                       dark:text-ink-400 dark:hover:bg-ink-700 dark:hover:text-paper-200"
          >
            {loading ? <Spinner className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" aria-hidden />}
            Re-check
          </button>
        </div>
      </div>
      <p className="mb-4 text-xs text-ink-500 dark:text-ink-400">
        Checked against the live site, not against what we meant to do. Only you see this.
      </p>

      {error && <p className="text-sm text-red-700 dark:text-red-400">{error}</p>}
      {!data && loading && <Spinner />}

      {data && (
        <ol className="space-y-4">
          {data.levels.map((level) => (
            <li key={level.level}>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="font-mono text-[11px] text-ink-400">L{level.level}</span>
                <h3 className="text-sm font-medium">{level.title}</h3>
                <ReadinessBadge state={level.state} />
              </div>
              <ul className="space-y-1.5 border-l border-paper-300 pl-3 dark:border-ink-700">
                {level.checks.map((check) => {
                  const style = STATE_STYLE[check.state];
                  const Icon = style.icon;
                  return (
                    <li key={check.key} className="flex gap-2 text-xs">
                      <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${style.className}`} aria-hidden />
                      <span>
                        <span className="font-medium">{check.label}</span>
                        <span className="block text-ink-500 dark:text-ink-400">{check.detail}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
